import { useRef } from "react";

import { gradientToCss } from "@excalidraw/element";

import type {
  ElementBackgroundGradient,
  ElementGradientStop,
} from "@excalidraw/element/types";

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/**
 * Horizontal gradient preview with draggable color stops.
 * - drag a stop to move its offset
 * - click the bar to add a stop
 * - double-click a stop to remove it (min 2 stops)
 */
export const GradientBar = ({
  gradient,
  selectedStop,
  onSelectStop,
  onChange,
}: {
  gradient: ElementBackgroundGradient;
  selectedStop: number;
  onSelectStop: (index: number) => void;
  onChange: (gradient: ElementBackgroundGradient) => void;
}) => {
  const barRef = useRef<HTMLDivElement>(null);
  const { stops } = gradient;

  const setStopOffset = (index: number, offset: number) => {
    const next = stops.map((s, i) =>
      i === index ? { ...s, offset: clamp01(offset) } : s,
    );
    onChange({ ...gradient, stops: next });
  };

  const handleStopPointerDown =
    (index: number) => (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onSelectStop(index);
      const bar = barRef.current;
      if (!bar) {
        return;
      }
      const el = event.currentTarget;
      el.setPointerCapture(event.pointerId);
      const move = (e: PointerEvent) => {
        const rect = bar.getBoundingClientRect();
        setStopOffset(index, (e.clientX - rect.left) / rect.width);
      };
      const up = () => el.removeEventListener("pointermove", move);
      el.addEventListener("pointermove", move);
      el.addEventListener("pointerup", up, { once: true });
      el.addEventListener("pointercancel", up, { once: true });
    };

  const addStopAt = (event: React.PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const offset = clamp01((event.clientX - rect.left) / rect.width);
    // seed the new stop with the nearest existing stop's color
    const nearest = stops.reduce((a, b) =>
      Math.abs(b.offset - offset) < Math.abs(a.offset - offset) ? b : a,
    );
    const newStop: ElementGradientStop = { offset, color: nearest.color };
    const next = [...stops, newStop];
    onChange({ ...gradient, stops: next });
    onSelectStop(next.length - 1);
  };

  const removeStop = (index: number) => {
    if (stops.length <= 2) {
      return;
    }
    const next = stops.filter((_, i) => i !== index);
    onChange({ ...gradient, stops: next });
    onSelectStop(Math.max(0, Math.min(selectedStop, next.length - 1)));
  };

  // preview bar always shows the stops laid out left→right
  const previewCss = gradientToCss({
    ...gradient,
    type: "linear",
    angle: 0,
  });

  return (
    <div
      ref={barRef}
      className="spectrum__gradient-bar"
      style={{ background: previewCss }}
      onPointerDown={(e) => {
        // click on empty bar area adds a stop
        if (e.target === e.currentTarget) {
          addStopAt(e);
        }
      }}
    >
      {stops.map((stop, index) => (
        <div
          key={index}
          className={`spectrum__gradient-stop${
            index === selectedStop ? " selected" : ""
          }`}
          style={{
            left: `${clamp01(stop.offset) * 100}%`,
            background: stop.color,
          }}
          onPointerDown={handleStopPointerDown(index)}
          onDoubleClick={() => removeStop(index)}
          title="Drag to move, double-click to remove"
        />
      ))}
    </div>
  );
};
